// Typed wrappers for the /v1/merchants/:id/memberships surface (Phase 06.1).
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient — the single
// axios instance that injects Authorization: Bearer <token> + X-Merchant-ID.
//
// Identity note: payouts-backend has no user table (identity is delegated to
// auth-service, no shared DB). A membership is (user_id, role) — the user_id
// is the auth-service user id and must be supplied by the caller.
import { apiClient } from "./client";

/** Per-merchant role. The global "admin" role lives in the JWT, not here. */
export type MembershipRole = "owner" | "operator" | "viewer";

export interface Membership {
  user_id: number;
  merchant_id: number;
  role: string;
  created_at: string;
  /**
   * Best-effort email from auth-service's user directory. Empty when the
   * caller's bearer lacks payouts:users:read OR auth-service is
   * unreachable — the panel falls back to rendering "User #<user_id>" in
   * that case.
   */
  email?: string;
  /**
   * Human-friendly name captured at invite time (migration 024). Stored
   * locally on the merchant_memberships row — distinct from email so
   * operators can render "Jane Doe" instead of "jane@acme.in". Empty for
   * pre-024 memberships; panel falls back to email, then "User #N".
   */
  display_name?: string;
}

/**
 * listMemberships — GET /v1/merchants/:merchantId/memberships
 * Returns the merchant's team. Requires viewer+ membership (admin short-circuits).
 */
export async function listMemberships(
  merchantId: number,
): Promise<Membership[]> {
  const resp = await apiClient.get<{ memberships: Membership[] }>(
    `/v1/merchants/${merchantId}/memberships`,
  );
  return resp.data.memberships;
}

/**
 * createMembership — POST /v1/merchants/:merchantId/memberships
 * Adds a user to the merchant team. Requires owner membership.
 */
export async function createMembership(
  merchantId: number,
  payload: { user_id: number; role: MembershipRole; display_name?: string },
): Promise<Membership> {
  const resp = await apiClient.post<Membership>(
    `/v1/merchants/${merchantId}/memberships`,
    payload,
  );
  return resp.data;
}

/**
 * updateMembership — PATCH /v1/merchants/:merchantId/memberships/:userId
 * Changes a member's role. Requires owner membership. The backend rejects
 * demoting the last owner with 409 last_owner.
 */
export async function updateMembership(
  merchantId: number,
  userId: number,
  role: MembershipRole,
): Promise<Membership> {
  const resp = await apiClient.patch<Membership>(
    `/v1/merchants/${merchantId}/memberships/${userId}`,
    { role },
  );
  return resp.data;
}

/**
 * deleteMembership — DELETE /v1/merchants/:merchantId/memberships/:userId
 * Removes a member. Requires owner membership. The backend rejects removing
 * the last owner with 409 last_owner.
 */
export async function deleteMembership(
  merchantId: number,
  userId: number,
): Promise<void> {
  await apiClient.delete(`/v1/merchants/${merchantId}/memberships/${userId}`);
}
